import { SaxesParser } from "saxes";
import type { Diagnostic, DomainObject, LossReport, ModelDocument } from "../shared/contracts.js";
import { createModel, createObject, syncDocument, uid, validateModel } from "../core/model.js";

export const XML_SOURCE_LIMIT = 5 * 1024 * 1024;
export const UML_NAMESPACE = "http://www.omg.org/spec/UML/20131001";
export const XMI_NAMESPACE = "http://www.omg.org/spec/XMI/20131001";
export interface XmlNode {
  name: string; local: string; uri: string; attrs: Record<string, string>;
  ns: Record<string, string>; children: XmlNode[]; text: string;
  start: number; end: number; line: number;
}
export interface XmlRead { roots: XmlNode[]; nodes: XmlNode[]; diagnostics: Diagnostic[] }
const attr = (n: XmlNode, name: string, uri = "") => n.attrs[`${uri}|${name}`];
export const xmlId = (n: XmlNode) => attr(n, "id", XMI_NAMESPACE);
export const xmlType = (n: XmlNode) => {
  if (n.uri && n.uri !== UML_NAMESPACE) return "";
  const value = attr(n, "type", XMI_NAMESPACE);
  if (!value) return n.uri === UML_NAMESPACE ? n.local : "";
  const [prefix, local] = value.split(":");
  return local && n.ns[prefix] === UML_NAMESPACE ? local : "";
};
const feature = (node: XmlNode, name: string) => (!node.uri || node.uri === UML_NAMESPACE) && node.local === name;

/** Strict streaming parser, deliberately no DTD, entity resolver, network or XInclude. */
export function readModelXml(source: string): XmlRead {
  const roots: XmlNode[] = [], nodes: XmlNode[] = [], stack: XmlNode[] = [];
  const diagnostics: Diagnostic[] = [];
  if (source.length > XML_SOURCE_LIMIT) return { roots, nodes, diagnostics: [{ code: "XML_SIZE_LIMIT", severity: "error", message: "XML review is limited to 5 MiB of text." }] };
  const parser = new SaxesParser({ xmlns: true, position: true });
  const lineStarts = [0];
  for (let i = 0; i < source.length; i++) if (source[i] === "\n") lineStarts.push(i + 1);
  const sourceLine = (position: number) => {
    let lo = 0, hi = lineStarts.length;
    while (lo + 1 < hi) { const mid = (lo + hi) >>> 1; if (lineStarts[mid] <= position) lo = mid; else hi = mid; }
    return lo + 1;
  };
  parser.on("doctype", () => { throw new Error("DOCTYPE and entity declarations are not accepted. Export XML without a DTD."); });
  parser.on("error", (error) => { throw error; });
  parser.on("opentag", (tag) => {
    if (stack.length >= 64 || nodes.length >= 50000) throw new Error("XML exceeds the 64-level / 50,000-element review limit.");
    const attrs: Record<string, string> = Object.create(null);
    for (const value of Object.values(tag.attributes)) attrs[`${value.uri}|${value.local}`] = value.value;
    const start = source.lastIndexOf("<", parser.position - 1);
    const node: XmlNode = { name: tag.name, local: tag.local, uri: tag.uri, attrs,
      ns: { ...(stack.at(-1)?.ns ?? {}), ...tag.ns }, children: [], text: "", start, end: parser.position,
      line: sourceLine(start) };
    (stack.at(-1)?.children ?? roots).push(node); nodes.push(node); stack.push(node);
  });
  parser.on("text", (text) => { if (stack.length) stack.at(-1)!.text += text; });
  parser.on("cdata", (text) => { if (stack.length) stack.at(-1)!.text += text; });
  parser.on("closetag", () => { const node = stack.pop(); if (node) node.end = parser.position; });
  try { parser.write(source).close(); }
  catch (error) { diagnostics.push({ code: "XML_PARSE_ERROR", severity: "error", line: parser.line,
    message: `XML could not be safely parsed: ${(error as Error).message}` }); }
  return { roots, nodes, diagnostics };
}

export const xmlModelBlocks = (parsed: XmlRead): XmlNode[] => {
  if (parsed.diagnostics.length) return [];
  const blocks: XmlNode[] = [];
  const walk = (node: XmlNode) => {
    if (["Model", "Package"].includes(xmlType(node)) || (node.uri === XMI_NAMESPACE && node.local === "XMI")) {
      node.children.forEach(walk);
    } else blocks.push(node);
  };
  parsed.roots.forEach(walk);
  return blocks;
};

export function importUmlXmi(source: string): { document: ModelDocument; diagnostics: Diagnostic[]; lossReport: LossReport } {
  const parsed = readModelXml(source);
  const document = createModel("Imported UML model", "logical", "relational");
  const diagnostics = [...parsed.diagnostics];
  const lossReport: LossReport = { preserved: [], translated: [], preservedOpaque: [], lost: [], unresolved: [] };
  const rep = document.model.activeRepresentation;
  document.representations[0].origin = "imported";
  const issue = (code: string, message: string, node?: XmlNode, severity: Diagnostic["severity"] = "warning") => {
    diagnostics.push({ code, message, severity, ...(node ? { line: node.line, path: xmlId(node) ?? node.name } : {}) });
    if (severity !== "info") lossReport.unresolved.push(message);
  };
  if (diagnostics.length) return { document, diagnostics, lossReport };
  const containers = parsed.nodes.filter(n => ["Model", "Package"].includes(xmlType(n)));
  const root = parsed.roots[0];
  if (!containers.length || !root || !(root.uri === XMI_NAMESPACE || root.uri === UML_NAMESPACE)) {
    issue("XML_UNSUPPORTED_FORMAT", "Expected OMG UML 2.5.1 class-model XML with UML 20131001 and XMI 20131001 namespaces. Other XML schemas require a separate adapter.", root, "error");
    return { document, diagnostics, lossReport };
  }
  document.model.name = attr(containers[0], "name") || "Imported UML model";
  const byId = new Map<string, XmlNode>();
  for (const node of parsed.nodes) {
    const id = xmlId(node);
    if (!id) continue;
    if (byId.has(id)) issue("XMI_DUPLICATE_ID", `Duplicate XMI identity ${id}; edit or exclude the duplicate before import.`, node, "error");
    else byId.set(id, node);
  }
  if (diagnostics.some(d => d.severity === "error")) return { document, diagnostics, lossReport };
  const description = (node: XmlNode) => node.children.filter(n => feature(n, "ownedComment"))
    .map(n => attr(n, "body") ?? n.children.find(c => feature(c, "body"))?.text ?? "").filter(Boolean).join("\n");
  const sourceMeta = (node: XmlNode) => ({ format: "uml-xmi", sourceId: xmlId(node), sourceLine: node.line, namespace: UML_NAMESPACE });
  const entities = new Map<string, DomainObject>();
  const modelBlocks = xmlModelBlocks(parsed);
  const classes = modelBlocks.filter(n => xmlType(n) === "Class");
  for (const node of classes) {
    const id = xmlId(node), name = attr(node, "name");
    if (!id || !name) { issue("XMI_CLASS_ID_NAME", "A UML class requires an XMI identity and name to become an entity.", node); continue; }
    const entity = createObject("entity", name, rep);
    entity.description = description(node); entity.extensions.importSource = sourceMeta(node);
    entities.set(id, entity); document.objects.push(entity);
  }
  const cardinality = (node: XmlNode) => {
    const lower = node.children.find(c => feature(c, "lowerValue"));
    const upper = node.children.find(c => feature(c, "upperValue"));
    // UML multiplicity defaults to 1; an explicitly present LiteralInteger with no value defaults to 0.
    const min = lower ? (attr(lower, "value") ?? "0") : (attr(node, "lower") ?? "1");
    const max = upper ? (attr(upper, "value") ?? "0") : (attr(node, "upper") ?? "1");
    if (!/^\d+$/.test(min) || !(max === "*" || /^\d+$/.test(max)) || (max !== "*" && Number(max) < Number(min)))
      issue("XMI_MULTIPLICITY", "Unsupported or inconsistent multiplicity; review the source bounds.", node);
    return { min, max, optional: Number(min) === 0, cardinality: max === "*" || Number(max) > 1 ? "many" as const : "one" as const };
  };
  for (const node of classes) {
    const owner = entities.get(xmlId(node) ?? ""); if (!owner) continue;
    for (const [index, property] of node.children.filter(n => feature(n, "ownedAttribute")).entries()) {
      const name = attr(property, "name");
      if (!name) { issue("XMI_PROPERTY_NAME", "Unnamed UML property was not projected.", property); continue; }
      const typeNode = property.children.find(n => feature(n, "type"));
      const typeRef = attr(property, "type") ?? (typeNode && attr(typeNode, "idref", XMI_NAMESPACE));
      const href = typeNode && attr(typeNode, "href");
      let typeName = typeRef ? attr(byId.get(typeRef) ?? property, "name") : undefined;
      if (typeRef && !byId.has(typeRef)) { typeName = undefined; issue("XMI_UNRESOLVED_TYPE", `${name}: type identity ${typeRef} is missing.`, property); }
      if (href) {
        if (/^https?:\/\/www\.omg\.org\/spec\/UML\/(20131001|20161101)\/PrimitiveTypes\.xmi#(String|Boolean|Integer|Real|UnlimitedNatural)$/.test(href)) typeName = href.split("#")[1];
        else issue("XMI_EXTERNAL_TYPE", `${name}: external type ${href} was retained as a reference; no external document was fetched.`, property);
      }
      const field = createObject("attribute", name, rep, owner.objectId);
      const bounds = cardinality(property);
      if (bounds.cardinality === "many" && !attr(property, "association")) issue("XMI_MULTIVALUED_ATTRIBUTE", `${name}: multiple values are preserved in source metadata; no relational child table was inferred.`, property);
      field.ordinal = index; field.nullable = bounds.optional; field.description = description(property);
      if (typeName) { field.type = { name: typeName }; field.logicalType = typeName; }
      field.extensions.importSource = { ...sourceMeta(property), typeRef, href, multiplicity: { lower: bounds.min, upper: bounds.max } };
      const association = attr(property, "association");
      if (association && !byId.has(association)) issue("XMI_UNRESOLVED_ASSOCIATION", `${name}: association ${association} is absent; property preserved without inventing a relationship.`, property);
      if (attr(property, "isID") === "true") issue("XMI_ID_REVIEW", `${name}: UML isID is retained for review; no business or primary key is inferred.`, property);
      document.objects.push(field);
    }
  }
  for (const association of modelBlocks.filter(n => xmlType(n) === "Association")) {
    const refs = (attr(association, "memberEnd") ?? "").split(/\s+/).filter(Boolean);
    refs.push(...association.children.filter(n => feature(n, "memberEnd")).map(n => attr(n, "idref", XMI_NAMESPACE) ?? "").filter(Boolean));
    let ends = refs.length ? refs.map(id => byId.get(id)) : association.children.filter(n => feature(n, "ownedEnd"));
    if (ends.length !== 2 || ends.some(e => !e)) { issue("XMI_ASSOCIATION_ENDS", `${attr(association, "name") || "Association"}: two resolvable member ends are required.`, association); continue; }
    const [a, b] = ends as XmlNode[];
    const sourceEntity = entities.get(attr(a, "type") ?? ""), targetEntity = entities.get(attr(b, "type") ?? "");
    if (!sourceEntity || !targetEntity) { issue("XMI_ASSOCIATION_TARGET", `${attr(association, "name") || "Association"}: an endpoint class is missing or excluded. No dangling relationship was created.`, association); continue; }
    const sourceBounds = cardinality(a), targetBounds = cardinality(b);
    document.relationships.push({ relationshipId: uid(), name: attr(association, "name") || `${sourceEntity.name}_${targetEntity.name}`,
      representationId: rep, sourceObjectId: sourceEntity.objectId, targetObjectId: targetEntity.objectId,
      sourceCardinality: sourceBounds.cardinality, targetCardinality: targetBounds.cardinality,
      sourceOptional: sourceBounds.optional, targetOptional: targetBounds.optional, identifying: false,
      description: description(association), extensions: { importSource: sourceMeta(association), umlEnds: [sourceMeta(a), sourceMeta(b)] } });
  }
  const known = new Set(["XMI", "Model", "Package", "Class", "Association", "PrimitiveType", "DataType", "Enumeration"]);
  const opaqueNodes = modelBlocks.filter(n => !known.has(xmlType(n)));
  for (const node of classes) opaqueNodes.push(...node.children.filter(n => !["ownedAttribute", "ownedComment"].some(name => feature(n, name))));
  for (const node of classes.flatMap(c => c.children.filter(n => feature(n, "ownedAttribute"))))
    opaqueNodes.push(...node.children.filter(n => !["ownedComment", "type", "lowerValue", "upperValue"].some(name => feature(n, name))));
  for (const node of opaqueNodes) {
    const reason = `${node.name}${attr(node, "name") ? ` ${attr(node, "name")}` : ""}: retained in source; outside the class/property/binary-association adapter.`;
    issue("XMI_OPAQUE_CONTENT", reason, node); lossReport.preservedOpaque.push(reason);
  }
  for (const node of [...classes, ...classes.flatMap(c => c.children.filter(n => feature(n, "ownedAttribute")))]) {
    const handled = new Set(["name", "type", "association", "lower", "upper"]);
    const extra = Object.keys(node.attrs).filter(k => k.startsWith("|") && !handled.has(k.slice(1)));
    if (extra.length) issue("XMI_PROPERTY_FLAGS", `${attr(node, "name") || node.local}: ${extra.map(k => k.slice(1)).join(", ")} retained in source; these UML flags do not have an equivalent ER property in this adapter.`, node);
  }
  document.diagrams[0].appearances = [...entities.values()].map((o, i) => ({ appearanceId: uid(), objectId: o.objectId, position: { x: 60 + (i % 4) * 400, y: 60 + Math.floor(i / 4) * 360 } }));
  document.diagrams[0].connectors = document.relationships.map(r => ({ relationshipId: r.relationshipId, routing: "auto" }));
  document.extensions.xmlImport = { adapter: "omg-uml-20131001", version: "1", source, externalResolution: "never" };
  syncDocument(document); diagnostics.push(...validateModel(document));
  lossReport.preserved.push(`${entities.size} UML classes`, `${document.relationships.length} binary associations`, "XMI source identities, descriptions and full original XML");
  lossReport.translated.push("UML classes/properties become logical entities/attributes. Multiplicity becomes cardinality/optionality; original bounds are retained. UML aggregation does not imply a relational identifying key.");
  return { document, diagnostics, lossReport };
}
